import React, { useRef, useState } from 'react';
import { Camera, User, Image as ImageIcon, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';

const OUT_SIZE = 512;

// Any photo size works: we centre-crop to a square and shrink to 512px JPEG on the device before upload,
// so 12 MB phone photos (and iPhone HEIC, which Safari hands over as JPEG) are fine.
const drawSquare = (source, w, h) => {
  const side = Math.min(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = OUT_SIZE; canvas.height = OUT_SIZE;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#FFFCF3'; ctx.fillRect(0, 0, OUT_SIZE, OUT_SIZE);
  ctx.drawImage(source, (w - side) / 2, (h - side) / 2, side, side, 0, 0, OUT_SIZE, OUT_SIZE);
  const out = canvas.toDataURL('image/jpeg', 0.86);
  return out.length > 480_000 ? canvas.toDataURL('image/jpeg', 0.7) : out; // server accepts up to 512 KB
};

const decodeWithImage = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => { try { resolve(drawSquare(img, img.naturalWidth, img.naturalHeight)); } catch (e) { reject(e); } finally { URL.revokeObjectURL(url); } };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
  img.src = url;
});

// iOS Safari: decode via <img> (handles HEIC → JPEG transcode it does for image/* inputs); if the browser
// can't decode the file, try createImageBitmap.
const fileToSquareJpeg = async (file) => {
  try { return await decodeWithImage(file); } catch { /* fall through */ }
  if (typeof createImageBitmap === 'function') {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const out = drawSquare(bmp, bmp.width, bmp.height);
      bmp.close?.();
      return out;
    } catch { /* fall through */ }
  }
  throw new Error('unreadable');
};

const ProfilePictureUpload = ({ currentPicture, onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [justSaved, setJustSaved] = useState(false);
  const [note, setNote] = useState(null); // { ok, text }
  const libraryRef = useRef(null);
  const cameraRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.type && !file.type.startsWith('image/') && !/\.(heic|heif|jpe?g|png|webp|gif)$/i.test(file.name || '')) {
      toast.error('Please choose a photo (JPG, PNG, HEIC or WebP).');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToSquareJpeg(file);
      setPreview(dataUrl);
      const ok = await onUpload(dataUrl);
      if (ok === false) throw new Error('save');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2200);
      setNote({ ok: true, text: 'Profile photo updated' });
      toast.success('Profile photo updated');
    } catch (e) {
      setPreview(null);
      const text = e.message === 'unreadable'
        ? "We couldn't read that photo. Try a JPG or PNG, or take a new one."
        : 'Could not save your photo — check your connection and try again.';
      setNote({ ok: false, text });
      toast.error(text);
    } finally {
      setUploading(false);
      if (libraryRef.current) libraryRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  };

  const shown = preview || currentPicture;
  const btn = 'inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-wide transition-colors active:scale-95 disabled:opacity-60';
  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={() => !uploading && libraryRef.current?.click()}
        aria-label={shown ? 'Change profile picture' : 'Add profile picture'}
        data-testid="profile-picture-tap"
        className="relative rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D6AD60] focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFFCF3] transition-transform active:scale-95"
        style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
      >
        <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-[#D6AD60] to-[#3a322a] flex items-center justify-center shadow-lg ring-2 ring-[#D6AD60]/40">
          {shown ? <img src={shown} alt="Profile" className="w-full h-full object-cover" data-testid="profile-picture-img" /> : <User className="w-12 h-12 text-[#1a1814]" />}
          {(uploading || justSaved) && (
            <span className="absolute inset-0 rounded-full flex items-center justify-center bg-[#1a1814]/55" data-testid="profile-picture-status">
              {uploading ? <Loader2 className="w-7 h-7 text-white animate-spin" /> : <Check className="w-8 h-8 text-[#D6AD60]" />}
            </span>
          )}
        </div>
        <span className="absolute bottom-0 right-0 w-8 h-8 bg-[#D6AD60] rounded-full flex items-center justify-center shadow-lg pointer-events-none" aria-hidden>
          <Camera className="w-4 h-4 text-[#1a1814]" />
        </span>
      </button>

      <input ref={libraryRef} type="file" accept="image/*" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" disabled={uploading} data-testid="profile-picture-input" />
      <input ref={cameraRef} type="file" accept="image/*" capture="user" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" disabled={uploading} data-testid="profile-picture-camera-input" />

      <div className="flex items-center gap-2">
        <button type="button" onClick={() => libraryRef.current?.click()} disabled={uploading} data-testid="profile-picture-change-btn"
          className={`${btn} bg-[#173F35] text-[#F8F6F0] border border-[#D6AD60]/55 hover:bg-[#245549]`} style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
          <ImageIcon className="w-3 h-3" /> {uploading ? 'Saving…' : shown ? 'Change photo' : 'Add photo'}
        </button>
        <button type="button" onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="profile-picture-camera-btn" aria-label="Take a photo"
          className={`${btn} bg-[#E8DDCB] text-[#173F35] border border-[#D6AD60]/40 hover:bg-[#DBC9A3]`} style={{ WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}>
          <Camera className="w-3 h-3" /> Take photo
        </button>
      </div>
      <p className={`text-[10px] -mt-1 ${note ? (note.ok ? 'text-emerald-700' : 'text-red-600') : 'text-stone-500'}`} data-testid="profile-picture-note">
        {note ? note.text : 'Any size photo works — we trim it to a circle for you.'}
      </p>
    </div>
  );
};

export default ProfilePictureUpload;
